/**
 * Operacoes e os avalistas da operacao.
 *
 * Reexportado por `../resources.ts`. Ver docs/FRONTEND.md, secao 3.
 */

import {
  query,
  type Address,
  type ListParams,
  type Money,
  type PartyRef,
} from './comum';

import { api, requestBlob, requestPage } from '../client';

/**
 * Situacao da cotacao (P-COT-1), mantida pelas transicoes da esteira. Ver
 * docs/ADR/0036-cotacao-na-operacao.md.
 */
export type ProposalStatus =
  | 'cotacao'
  | 'em_analise'
  | 'aguardando_corretora'
  | 'aguardando_emissao_corretora'
  | 'aguardando_emissao_seguradora'
  | 'aguardando_pagamento_boleto'
  | 'emitida'
  | 'recusada'
  | 'rejeitada'
  | 'cancelada';

/** Tipo da cotacao, definido na abertura. */
export type ProposalType = 'apolice' | 'endosso' | 'renovacao';

/**
 * Tomador e corretora na operacao, com o CPF ou CNPJ sempre visivel. O tomador traz o
 * endereco do cadastro no detalhe, para quem le o tomador (NCT-090 e NCT-166).
 */
export type ProposalPartyRef = PartyRef & { document?: string; address?: Address };

/**
 * Campos da cotacao na operacao (P-COT-1). Opcionais no tipo para que respostas
 * antigas e simulacoes de teste continuem validas.
 */
export interface ProposalFields {
  proposal_number?: string | null;
  proposal_status?: ProposalStatus;
  proposal_type?: ProposalType;
  endorsement_type_id?: string | null;
  /** "Prazo (dias)", como digitado. A conversao com inicio e fim e PRC-008. */
  term_days?: number | null;
  period_type?: 'dias' | 'meses' | 'anos';
  complementary_modality?: PartyRef | null;
  /** Data civil AAAA-MM-DD. */
  complementary_coverage_start?: string | null;
  insured_trade_name?: { id: string | null; name: string } | null;
  premium_locked?: boolean;
  big_risk?: boolean;
  monitoring?: boolean;
  step_in?: boolean;
  premium_set_to_zero?: boolean;
  premium_set_manually?: boolean;
  principal_premium?: Money | null;
  complementary_premium?: Money | null;
  min_premium?: Money | null;
  max_commission_bps?: number | null;
  subscription_in_progress?: boolean;
  created_by_label?: string;
  updated_by_label?: string;
}

/** O que o Resumo mostra da modalidade, lido do cadastro (P-PAR-1). */
export interface ProposalModalityInfo {
  id: string;
  code: string;
  reference_code: string;
  group: PartyRef | null;
  credit_group: string;
  settings_version: number | null;
  allows_step_in: boolean;
  is_monitored: boolean;
  is_large_risk: boolean;
  allows_complementary: boolean;
  is_complementary: boolean;
  equalize_terms: boolean;
  min_premium: Money | null;
  min_term_days: number | null;
  max_term_days: number | null;
}

export interface ProposalTradeNameOption {
  id: string;
  name: string;
  is_main: boolean;
}

export interface Operation extends ProposalFields, QuoteFields {
  id: string;
  number: number;
  stage: string;
  stage_position: number;
  is_terminal: boolean;
  is_overdue: boolean;
  decline_reason: string;
  subject: string;
  notes: string;
  policyholder: ProposalPartyRef;
  broker: ProposalPartyRef | null;
  /** Quem responde pela operacao. Vazio quando ninguem assumiu. */
  owner_label: string;
  /** Responsavel e prioridade (P-EST-1). Opcionais para respostas antigas. */
  owner_member_id?: string | null;
  priority?: string;
  product: PartyRef;
  modality: PartyRef;
  /**
   * Segurado e endereco do segurado (P-SEG-1). Opcionais no tipo para que respostas
   * antigas e simulacoes de teste continuem validas; nulos na operacao sem segurado.
   */
  insured?: { id: string; label: string; document: string } | null;
  insured_address?: { id: string; label: string } | null;
  insured_amount: Money;
  applied_rate_bps: number;
  premium: Money;
  commission_bps: number;
  coverage_start: string | null;
  coverage_end: string | null;
  stage_due_at: string | null;
  created_at: string;
  updated_at: string;
  /** Fase e situacao atual da linha do tempo (P-COT-3). Opcionais para respostas antigas. */
  phase?: TimelinePhase;
  timeline_situation?: TimelineSituation | null;
}

export interface TransitionOption {
  stage: string;
  label: string;
  permission: string;
  /** Parecer que o movimento exige aprovado: credito, subscricao ou juridico. */
  requires?: '' | 'credito' | 'subscricao' | 'juridico';
  /**
   * Por que o movimento ainda nao passa, lido do parecer. Vazio quando passa; o pedido
   * de mudanca de etapa confere de novo.
   */
  blocked_reason?: string;
}

export interface TimelineEntry {
  id: string;
  kind: string;
  from_stage: string;
  to_stage: string;
  summary: string;
  detail?: Record<string, unknown>;
  actor_label: string;
  created_at: string;
  /** Situacao, grupo, status e lado (P-COT-3). Opcionais para respostas antigas. */
  situation?: string;
  situation_group?: TimelineGroup;
  situation_status?: TimelineStatus;
  actor_side?: TimelineSide;
}

export interface OperationDetail extends Operation {
  progression: string[];
  transitions: TransitionOption[];
  timeline: TimelineEntry[];
  /** Do Resumo da cotacao (P-COT-1). */
  modality_info?: ProposalModalityInfo | null;
  complementary_modality_info?: ProposalModalityInfo | null;
  insured_trade_names?: ProposalTradeNameOption[];
  /** O que a sessao pode pedir na cotacao (P-NCT). Opcional para respostas antigas. */
  quote_actions?: QuoteActions;
}

export interface OperationInput {
  policyholder_id?: string;
  broker_id?: string;
  product_id?: string;
  modality_id?: string;
  /** Vazio limpa, ausente mantem, como o corretor. */
  insured_id?: string;
  insured_address_id?: string;
  insured_amount?: Money;
  applied_rate_bps?: number;
  commission_bps?: number;
  coverage_start?: string;
  coverage_end?: string;
  stage_due_at?: string;
  subject?: string;
  notes?: string;
  // Campos da cotacao (P-COT-1). Ausente mantem; nulo ou vazio limpa.
  proposal_number?: string;
  proposal_type?: ProposalType;
  endorsement_type_id?: string;
  term_days?: number | null;
  period_type?: 'dias' | 'meses' | 'anos';
  complementary_modality_id?: string;
  complementary_coverage_start?: string;
  insured_trade_name_id?: string;
  premium_locked?: boolean;
  big_risk?: boolean;
  monitoring?: boolean;
  step_in?: boolean;
  premium_set_to_zero?: boolean;
  premium_set_manually?: boolean;
  principal_premium?: Money | null;
  complementary_premium?: Money | null;
  min_premium?: Money | null;
  max_commission_bps?: number | null;
  /** Filial do tomador na cotacao (P-NCT). Vazio limpa, ausente mantem. */
  policyholder_branch_id?: string;
  /**
   * Codigo do quadro de distribuicao (NCT-215), texto opcional ate 60 caracteres.
   * Vazio limpa, ausente mantem; a obrigatoriedade nao tem fonte (RES-005).
   */
  distribution_code?: string;
  /**
   * So na abertura (ADR 0103): os valores das TAGs do objeto, gravados no bloco do objeto
   * da cotacao. Com o campo presente, o servidor exige as TAGs obrigatorias.
   */
  object_tag_values?: Record<string, string>;
  /** So na abertura: a forma de pagamento, entre os meios ativos da empresa. */
  payment_method_id?: string;
}

/** Cobertura adicional da cotacao (P-COT-1). */
export interface ProposalCoverage {
  id: string;
  coverage_id: string | null;
  name: string;
  position: number;
  insured_amount: Money;
  premium: Money | null;
  coverage_start: string | null;
  term_days: number | null;
  coverage_end: string | null;
  insured_amount_calc: string;
  premium_calc: string;
  calc_params: Record<string, string>;
  created_by_label: string;
  updated_by_label: string;
  created_at: string;
  updated_at: string;
}

export interface ProposalCoverageInput {
  id?: string;
  coverage_id?: string;
  name: string;
  insured_amount: Money;
  premium?: Money;
  coverage_start?: string;
  term_days?: number;
  coverage_end?: string;
  insured_amount_calc?: string;
  premium_calc?: string;
  calc_params?: Record<string, string>;
}

/** Configuracao da cotacao da empresa: numero e situacao do declinio. */
export interface ProposalSettings {
  number_mode: 'automatico' | 'informado';
  number_prefix: string;
  declined_status: 'recusada' | 'rejeitada' | 'cancelada';
  updated_by_label?: string;
  updated_at?: string | null;
  /** Etapa em que a cotacao vira proposta na linha do tempo (P-COT-3). */
  timeline_proposal_stage?: string;
  /**
   * Comissao padrao da empresa em pontos-base (2000 = 20%), a sugestao do campo Comissao
   * quando a corretora nao tem percentual cadastrado (OPEN_QUESTIONS 2.97). Ausente no
   * pedido, o servidor mantem a gravada.
   */
  default_commission_bps?: number;
}

/**
 * Execucao do motor de credito da operacao, para a aba "Historico do motor". A rota
 * e a do motor (`GET /api/v1/operations/{id}/credit-engine/runs`).
 */
export interface OperationEngineRun {
  id: string;
  operation_id: string;
  engine_version: string;
  mode: string;
  reference_at: string;
  final_result: string;
  calculated_result: string;
  pendency_count: number;
  simulated: boolean;
  notice?: string;
  requested_label?: string;
  parameter_set?: { id: string; version: number; status_at_run: string };
  created_at: string;
}

/** Resposta do fim da vigencia pelo prazo, bloqueado ate a regra (PRC-008). */
export interface ProposalCoverageDates {
  start: string | null;
  term_days: number | null;
  coverage_end: string | null;
  calculated: boolean;
  pending_rule: string;
  message: string;
}

export const operations = {
  list: (params: ListParams, signal?: AbortSignal) =>
    requestPage<Operation>(`/api/v1/operations${query(params)}`, signal),
  get: (id: string, signal?: AbortSignal) =>
    api.get<OperationDetail>(`/api/v1/operations/${id}`, signal),
  create: (input: OperationInput) =>
    api.post<OperationDetail>('/api/v1/operations', input),
  update: (id: string, input: OperationInput) =>
    api.patch<OperationDetail>(`/api/v1/operations/${id}`, input),
  transition: (id: string, stage: string, reason?: string) =>
    api.post<OperationDetail>(`/api/v1/operations/${id}/transitions`, {
      stage,
      ...(reason ? { reason } : {}),
    }),
  // Cotacao na operacao (P-COT-1).
  coverages: (id: string, signal?: AbortSignal) =>
    api.get<ProposalCoverage[]>(`/api/v1/operations/${id}/coverages`, signal),
  replaceCoverages: (id: string, coverages: ProposalCoverageInput[]) =>
    api.put<ProposalCoverage[]>(`/api/v1/operations/${id}/coverages`, { coverages }),
  proposalSettings: (signal?: AbortSignal) =>
    api.get<ProposalSettings>('/api/v1/operations/proposal-settings', signal),
  updateProposalSettings: (input: ProposalSettings) =>
    api.put<ProposalSettings>('/api/v1/operations/proposal-settings', {
      number_mode: input.number_mode,
      number_prefix: input.number_prefix,
      declined_status: input.declined_status,
      ...(input.timeline_proposal_stage
        ? { timeline_proposal_stage: input.timeline_proposal_stage }
        : {}),
      ...(input.default_commission_bps !== undefined
        ? { default_commission_bps: input.default_commission_bps }
        : {}),
    }),
  engineRuns: (id: string, signal?: AbortSignal) =>
    api.get<OperationEngineRun[]>(
      `/api/v1/operations/${id}/credit-engine/runs`,
      signal,
    ),
  coverageDates: (start: string, termDays: number | null, signal?: AbortSignal) =>
    api.get<ProposalCoverageDates>(
      `/api/v1/operations/coverage-dates${query({ start, term_days: termDays ?? undefined })}`,
      signal,
    ),
};

/** Avalista de uma operacao. */
export interface Guarantor {
  id: string;
  operation_id: string;
  name: string;
  document: string;
  document_kind: string;
  participation_bps: number;
  email: string;
  phone: string;
  notes: string;
  created_at: string;
  updated_at: string;
}

export interface GuarantorInput {
  name?: string;
  document?: string;
  document_kind?: string;
  participation_bps?: number;
  email?: string;
  phone?: string;
  notes?: string;
}

export const guarantors = {
  list: (operationId: string, signal?: AbortSignal) =>
    api.get<Guarantor[]>(`/api/v1/operations/${operationId}/guarantors`, signal),
  create: (operationId: string, input: GuarantorInput) =>
    api.post<Guarantor>(`/api/v1/operations/${operationId}/guarantors`, input),
  update: (operationId: string, guarantorId: string, input: GuarantorInput) =>
    api.patch<Guarantor>(
      `/api/v1/operations/${operationId}/guarantors/${guarantorId}`,
      input,
    ),
  remove: (operationId: string, guarantorId: string) =>
    api.delete<void>(`/api/v1/operations/${operationId}/guarantors/${guarantorId}`),
};

// --- Termos contratuais, clausulas particulares e minuta da cotacao (P-COT-2) ---------
// Ver docs/ADR/0041-termos-contratuais-da-cotacao.md.

/**
 * Bloco da cotacao: uma aba do editor "Objetos" do OnPoint ou uma clausula particular
 * escolhida. As condicoes especiais sem cobertura sao as da propria modalidade.
 */
export type ContractualTermKind =
  | 'objeto_modalidade'
  /** O objeto da modalidade complementar escolhida na cotacao (NCT-141). */
  | 'objeto_complementar'
  | 'objeto_cobertura'
  | 'condicoes_gerais'
  | 'condicoes_especiais'
  | 'clausula_particular';

/** TAG declarada no texto copiado da modalidade. */
export interface ContractualTermTag {
  label: string;
  tag_key: string;
  input_kind: string;
}

/**
 * O objeto que a abertura vai copiar da modalidade (ADR 0103). `enabled` falso: sem
 * objeto em vigor, ou fora da cotacao.
 */
export interface OpeningObject {
  enabled: boolean;
  body: string;
  tags: ContractualTermTag[];
}

/** A clausula no cadastro da modalidade, com as marcacoes atuais (SUB-014). */
export interface ContractualTermClause {
  id: string;
  name: string;
  version: number;
  active: boolean;
  broker_can_select: boolean;
  automatic_on_underwriting: boolean;
  enabled_on_quote: boolean;
  free_text: boolean;
  aggregator_clause: string | null;
  policyholder_restricted: boolean;
}

/** Bloco da cotacao, copiado da versao em vigor do texto da modalidade. */
export interface ContractualTerm {
  id: string;
  kind: ContractualTermKind;
  operation_coverage_id: string | null;
  coverage_name: string | null;
  modality_id: string | null;
  additional_coverage_id: string | null;
  /** A versao do texto ou a clausula de onde o bloco veio. */
  contractual_term_id: string | null;
  template_version: number | null;
  name: string;
  position: number;
  insurer_reference: string | null;
  /** HTML saneado no servidor, com as marcacoes {{grupo.chave}}. */
  body: string;
  /**
   * O texto do modelo que o objeto copiou, "Dados do objeto da apolice inicial"
   * (NCT-140). Nulo fora dos objetos.
   */
  original_body?: string | null;
  characters: number;
  /** O texto saiu do copiado alem das TAGs. */
  modified: boolean;
  /** "Habilitar na cotacao": desabilitado, o bloco nao sai na minuta. */
  enabled: boolean;
  free_text: boolean;
  body_editable: boolean;
  tags: ContractualTermTag[];
  tag_values: Record<string, string>;
  max_characters: number;
  particular_clause: ContractualTermClause | null;
  created_by_label: string;
  updated_by_label: string;
  created_at: string;
  updated_at: string;
}

/** O editor da cotacao: as marcas e os blocos, na ordem das abas do OnPoint. */
export interface ContractualTerms {
  operation_id: string;
  stage: string;
  /** A etapa ainda aceita mudar os textos (ate a etapa comercial). */
  editable: boolean;
  /** "Objeto totalmente modificado". So marca: a regra e SUB-011, conflitante. */
  object_modified: boolean;
  /** "Disponibilizar minuta condicionada". Guardado sem efeito (COT-200). */
  conditional_draft_enabled: boolean;
  /**
   * A cotacao tem modalidade complementar sem objeto em vigor: "Objeto da modalidade
   * complementar nao cadastrado." (NCT-141).
   */
  complementary_object_missing?: boolean;
  items: ContractualTerm[];
}

/** Edicao de um bloco: o campo ausente fica como esta. */
export interface ContractualTermInput {
  body?: string;
  enabled?: boolean;
  tag_values?: Record<string, string>;
}

/** Opcao do dialogo "Condicoes Particulares". */
export interface ParticularClauseChoice extends ContractualTermClause {
  selected: boolean;
  tags: ContractualTermTag[];
}

/** Cobertura adicional do cadastro da modalidade da cotacao. */
export interface AdditionalCoverageOption {
  id: string;
  name: string;
  insurer_reference_code: string | null;
}

export const contractualTerms = {
  /** O objeto da modalidade, para a Nova operacao pedir as TAGs antes de abrir. */
  openingObject: (modalityId: string, signal?: AbortSignal) =>
    api.get<OpeningObject>(
      `/api/v1/operations/opening-object${query({ modality_id: modalityId })}`,
      signal,
    ),
  get: (operationId: string, signal?: AbortSignal) =>
    api.get<ContractualTerms>(
      `/api/v1/operations/${operationId}/contractual-terms`,
      signal,
    ),
  update: (operationId: string, termId: string, input: ContractualTermInput) =>
    api.put<ContractualTerms>(
      `/api/v1/operations/${operationId}/contractual-terms/${termId}`,
      input,
    ),
  assemble: (operationId: string) =>
    api.post<ContractualTerms>(
      `/api/v1/operations/${operationId}/contractual-terms/assembly`,
      {},
    ),
  setConditionalDraft: (operationId: string, enabled: boolean) =>
    api.patch<ContractualTerms>(`/api/v1/operations/${operationId}/contractual-terms`, {
      conditional_draft_enabled: enabled,
    }),
  clauseChoices: (operationId: string, signal?: AbortSignal) =>
    api.get<ParticularClauseChoice[]>(
      `/api/v1/operations/${operationId}/particular-clauses`,
      signal,
    ),
  replaceClauses: (operationId: string, clauseIds: string[]) =>
    api.put<ContractualTerms>(`/api/v1/operations/${operationId}/particular-clauses`, {
      clause_ids: clauseIds,
    }),
  coverageOptions: (operationId: string, signal?: AbortSignal) =>
    api.get<AdditionalCoverageOption[]>(
      `/api/v1/operations/${operationId}/additional-coverage-options`,
      signal,
    ),
};

// --- Acompanhamento da cotacao (P-COT-3) ------------------------------------------
// Ver docs/ADR/0046-acompanhamento-da-cotacao.md.

/** Fase da linha do tempo (Cotacao, Proposta, Emissao). */
export type TimelinePhase = 'cotacao' | 'proposta' | 'emissao';

/** Grupo da situacao (Cadastro, PEP, Credito, Subscricao, Resseguro, Emissao...). */
export type TimelineGroup =
  | 'cotacao'
  | 'cadastro'
  | 'pep'
  | 'credito'
  | 'subscricao'
  | 'resseguro'
  | 'juridico'
  | 'comercial'
  | 'emissao';

/** Status do item (legenda Aprovado e Recusado do OnPoint). */
export type TimelineStatus =
  | 'aprovado'
  | 'recusado'
  | 'pendente'
  | 'resolvido'
  | 'em_andamento'
  | 'cancelado'
  | 'informativo';

/** Lado de quem executou (legenda Seguradora e Corretor do OnPoint). */
export type TimelineSide = 'seguradora' | 'corretor' | 'sistema';

export interface TimelineSituation {
  situation: string;
  situation_group: TimelineGroup;
  situation_status: TimelineStatus;
  actor_side: TimelineSide;
  at: string | null;
}

export interface QuoteTimeline {
  operation_id: string;
  phase: TimelinePhase;
  phases: {
    key: TimelinePhase;
    label: string;
    state: 'concluida' | 'atual' | 'interrompida' | 'pendente';
  }[];
  proposal_stage: string;
  current: TimelineSituation | null;
  items: TimelineEntry[];
}

export interface TimelineInfoEntry {
  id: string;
  text: string;
  situation_group: TimelineGroup;
  actor_label: string;
  actor_side: TimelineSide;
  created_at: string;
}

/** Documento da operacao como o acompanhamento o mostra. */
export interface TimelineDocument {
  id: string;
  title: string;
  group: string;
  required: boolean;
  status: 'pendente' | 'recebido' | 'dispensado';
  current_version: number;
  origin: 'corretor' | 'seguradora' | 'sistema' | null;
  requested_at: string | null;
  requested_label: string;
  request_note: string;
  created_at: string;
}

export interface QuoteTimelineInfo {
  entries: TimelineInfoEntry[];
  requested_documents: TimelineDocument[];
  has_pending_documents: boolean;
}

/** Motivo da linha do historico de alteracoes. */
export type TimelineHistoryKind = 'abertura' | 'alteracao' | 'reinicio' | 'carga';

/** Linha do historico dos dados da modalidade ("Ver alteracoes"). */
export interface ModalityHistoryEntry {
  id: string;
  kind: TimelineHistoryKind;
  insured_amount: Money;
  coverage_start: string | null;
  coverage_end: string | null;
  term_days: number | null;
  complementary_coverage_start: string | null;
  insured_trade_name_id: string | null;
  insured_trade_name: string;
  author_label: string;
  created_at: string;
}

/** Linha do historico do risco: premio, comissao e taxa gravados. */
export interface RiskChange {
  id: string;
  kind: TimelineHistoryKind;
  insured_amount: Money;
  premium: Money;
  principal_premium: Money | null;
  rate_bps: number;
  commission_bps: number;
  author_label: string;
  created_at: string;
}

/** Motivo do cancelamento ou da negativa: o padronizado, o texto ou os dois. */
export interface QuoteDeclineInput {
  reason?: string;
  decline_reason_id?: string;
}

export interface QuoteDocumentRequestInput {
  title: string;
  group?: string;
  document_type_id?: string;
  note?: string;
  area?: string;
}

export const quoteTimeline = {
  timeline: (operationId: string, signal?: AbortSignal) =>
    api.get<QuoteTimeline>(`/api/v1/operations/${operationId}/timeline`, signal),
  info: (operationId: string, signal?: AbortSignal) =>
    api.get<QuoteTimelineInfo>(
      `/api/v1/operations/${operationId}/timeline-info`,
      signal,
    ),
  addInfo: (operationId: string, input: { text: string; situation_group?: string }) =>
    api.post<TimelineInfoEntry>(
      `/api/v1/operations/${operationId}/timeline-info`,
      input,
    ),
  modalityHistory: (operationId: string, signal?: AbortSignal) =>
    api.get<ModalityHistoryEntry[]>(
      `/api/v1/operations/${operationId}/modality-history`,
      signal,
    ),
  riskChanges: (operationId: string, signal?: AbortSignal) =>
    api.get<RiskChange[]>(`/api/v1/operations/${operationId}/risk-changes`, signal),
  resetModalityHistory: (operationId: string) =>
    api.post<ModalityHistoryEntry>(
      `/api/v1/operations/${operationId}/modality-history/reset`,
    ),
  resetRiskChanges: (operationId: string) =>
    api.post<RiskChange>(`/api/v1/operations/${operationId}/risk-changes/reset`),
  cancel: (operationId: string, input: QuoteDeclineInput) =>
    api.post<OperationDetail>(`/api/v1/operations/${operationId}/cancellation`, input),
  deny: (operationId: string, input: QuoteDeclineInput) =>
    api.post<OperationDetail>(`/api/v1/operations/${operationId}/denial`, input),
  requestDocument: (operationId: string, input: QuoteDocumentRequestInput) =>
    api.post<TimelineDocument>(
      `/api/v1/operations/${operationId}/document-requests`,
      input,
    ),
  pendingDocuments: (operationId: string, signal?: AbortSignal) =>
    api.get<TimelineDocument[]>(
      `/api/v1/operations/${operationId}/documents/pending`,
      signal,
    ),
  /** "Baixar todos": o ZIP com o arquivo corrente de cada documento da operacao. */
  documentsArchive: (operationId: string) =>
    requestBlob(`/api/v1/operations/${operationId}/documents/archive`),
};

// --- Financeiro, emissao e validacoes automaticas da cotacao (P-COT-4) ---------------
// Ver docs/ADR/0051-financeiro-da-cotacao.md. Nada aqui calcula:
// parcelas, participacoes e marcas sao as informadas por alcada, e as rotas de calculo
// e de validacao respondem 409 com as regras pendentes.

/** Acao bloqueada ate a homologacao das regras dela. */
export interface QuoteRuleSet {
  available: boolean;
  action: string;
  pending_rules: string[];
  message: string;
}

/** Quem gera o boleto, pela configuracao da apolice (P-APO-1). */
export type QuoteBilletMode = 'desligado' | 'anexo';

/** Parcela prevista da cotacao, informada por alcada. */
export interface QuoteInstallment {
  id: string;
  number: number;
  amount: Money;
  interest: Money;
  due_on: string;
  billet: {
    document_id: string;
    number: string;
    attached_label: string;
    attached_at: string | null;
  } | null;
  billet_number: string;
  source: 'manual' | 'integracao';
  created_label: string;
  updated_label: string;
  created_at: string;
  updated_at: string;
}

export interface QuoteInstallmentList {
  operation_id: string;
  stage: string;
  installments_count: number | null;
  locked: boolean;
  /** A etapa ainda aceita mudar as parcelas (ate a etapa comercial). */
  editable: boolean;
  billet_mode: QuoteBilletMode;
  single_billet: boolean;
  /** Soma do informado, so para conferencia (PAR-002). */
  total: Money;
  first_installment: { number: number; amount: Money; due_on: string } | null;
  items: QuoteInstallment[];
}

export interface QuoteInstallmentInput {
  number: number;
  amount: Money;
  interest?: Money | null;
  due_on: string;
  billet_number?: string;
}

export interface QuoteInstallmentBillet {
  number: number;
  billet_mode: QuoteBilletMode;
  available: boolean;
  reason: string;
  document_id: string | null;
  billet_number: string;
  installment_numbers: number[];
  url: string;
  filename: string;
  expires_at: string | null;
}

export interface QuoteFinancialParty {
  id: string;
  label: string;
  sublabel: string;
  active: boolean;
}

export interface QuoteCoBroker {
  id: string;
  broker: QuoteFinancialParty;
  share_bps: number;
  position: number;
  created_label: string;
  updated_label: string;
}

export interface QuoteReinsurerShare {
  id: string;
  reinsurer: QuoteFinancialParty;
  share_bps: number;
  position: number;
  created_label: string;
  updated_label: string;
}

export interface QuoteTermsAcceptance {
  accepted: boolean;
  accepted_at: string | null;
  accepted_by: string | null;
  accepted_label: string;
  version: number | null;
  acceptance_id: string | null;
}

export type QuoteCoinsuranceType = 'cedido' | 'aceito';
export type QuoteIssuanceKind = 'automatica' | 'manual';

/** O financeiro e a emissao da cotacao. */
export interface QuoteFinancial {
  operation_id: string;
  stage: string;
  installments_count: number | null;
  installments_locked: boolean;
  /**
   * Forma de pagamento escolhida (NCT-152, P-NCT-L): o meio de pagamento da empresa e o
   * rotulo dele no momento da escolha. Sem efeito em premio ou parcelas.
   */
  payment_method_id: string | null;
  payment_method_label: string;
  manual_policy_issuance: boolean;
  manual_billet_issuance: boolean;
  issuance_tags: Record<string, unknown>;
  issuance_tags_updated: boolean;
  coinsurance_type: QuoteCoinsuranceType | null;
  issuance_kind: QuoteIssuanceKind | null;
  related_external_id: string | null;
  terms_acceptance: QuoteTermsAcceptance;
  issuance_requires_terms_acceptance: boolean;
  premium: Money;
  min_premium: Money | null;
  /** O premio e igual ao minimo informado (fato; PRC-002 pendente). */
  premium_is_minimum: boolean;
  premium_insured_ratio_limit_bps: number | null;
  plan_editable: boolean;
  issuance_editable: boolean;
  installments: QuoteInstallmentList;
  co_brokers: QuoteCoBroker[];
  reinsurers: QuoteReinsurerShare[];
  calculation: QuoteRuleSet;
}

/** Mudanca parcial: o campo ausente fica como esta; null limpa. */
export interface QuoteFinancialInput {
  installments_count?: number | null;
  /** Meio de pagamento da empresa; null limpa (P-NCT-L). */
  payment_method_id?: string | null;
  installments_locked?: boolean;
  manual_policy_issuance?: boolean;
  manual_billet_issuance?: boolean;
  coinsurance_type?: QuoteCoinsuranceType | null;
  issuance_kind?: QuoteIssuanceKind | null;
  related_external_id?: string | null;
}

export interface QuoteFinancialSettings {
  issuance_requires_terms_acceptance: boolean;
  premium_insured_ratio_limit_bps: number | null;
  updated_label: string;
  updated_at: string | null;
}

export interface QuoteModalityLimits {
  settings_version: number | null;
  min_term_days: number | null;
  max_term_days: number | null;
  max_future_days: number | null;
  max_retroactive_days: number | null;
  retroactivity_handling: string | null;
  retroactive_pipe_enabled: boolean;
}

export interface QuoteRetroactivity {
  operation_id: string;
  validated: boolean;
  coverage_start: string | null;
  reference_date: string;
  starts_before_reference: boolean;
  modality_limits: QuoteModalityLimits | null;
  rules: QuoteRuleSet;
}

export interface QuoteCoinsurance {
  operation_id: string;
  validated: boolean;
  coinsurance_type: string | null;
  coinsurers: {
    insurer_id: string;
    name: string;
    share_bps: number | null;
    active: boolean;
  }[];
  rules: QuoteRuleSet;
}

/** Uma mudanca do tipo de cosseguro da cotacao (COT-451). */
export interface QuoteCoinsuranceHistoryEntry {
  id: string;
  previous_type: QuoteCoinsuranceType | null;
  coinsurance_type: QuoteCoinsuranceType | null;
  changed_by: string | null;
  changed_label: string;
  changed_at: string;
}

export interface QuoteCoinsuranceHistory {
  operation_id: string;
  coinsurance_type: QuoteCoinsuranceType | null;
  items: QuoteCoinsuranceHistoryEntry[];
  rules: QuoteRuleSet;
}

/** O cosseguro da apolice ou do endosso, como foi gravado (COT-447, COT-448). */
export interface QuoteCoinsuranceObject {
  policy_id: string;
  endorsement_id: string | null;
  validated: boolean;
  coinsurance_type: QuoteCoinsuranceType | null;
  coinsurers: {
    insurer_id: string | null;
    name: string;
    document: string;
    share_bps: number;
    is_leader: boolean;
    position: number;
  }[];
  rules: QuoteRuleSet;
}

export interface QuoteTermCheck {
  modality_id: string;
  validated: boolean;
  term_days: number | null;
  modality_limits: QuoteModalityLimits;
  rules: QuoteRuleSet;
}

/** Validacao automatica bloqueada ate a homologacao. */
export type QuoteValidationKind =
  'pep-validation' | 'reinsurance-validation' | 'underwriting-validation';

const financeiroDaCotacao = (operationId: string, resto = '') =>
  `/api/v1/operations/${operationId}${resto}`;

export const quoteFinancial = {
  get: (operationId: string, signal?: AbortSignal) =>
    api.get<QuoteFinancial>(financeiroDaCotacao(operationId, '/financial'), signal),
  update: (operationId: string, input: QuoteFinancialInput) =>
    api.patch<QuoteFinancial>(financeiroDaCotacao(operationId, '/financial'), input),
  calculate: (operationId: string, force = false) =>
    api.post<void>(
      financeiroDaCotacao(
        operationId,
        `/financial-calculation${force ? '?force=true' : ''}`,
      ),
      {},
    ),
  installments: (operationId: string, signal?: AbortSignal) =>
    api.get<QuoteInstallmentList>(
      financeiroDaCotacao(operationId, '/installments'),
      signal,
    ),
  replaceInstallments: (operationId: string, installments: QuoteInstallmentInput[]) =>
    api.put<QuoteInstallmentList>(financeiroDaCotacao(operationId, '/installments'), {
      installments,
    }),
  billet: (operationId: string, number: number, signal?: AbortSignal) =>
    api.get<QuoteInstallmentBillet>(
      financeiroDaCotacao(operationId, `/installments/${number}/billet`),
      signal,
    ),
  attachBillet: (
    operationId: string,
    number: number,
    input: { document_id: string | null; billet_number?: string },
  ) =>
    api.post<QuoteInstallmentBillet>(
      financeiroDaCotacao(operationId, `/installments/${number}/billet`),
      input,
    ),
  replaceCoBrokers: (
    operationId: string,
    coBrokers: { broker_id: string; share_bps: number }[],
  ) =>
    api.put<QuoteCoBroker[]>(financeiroDaCotacao(operationId, '/co-brokers'), {
      co_brokers: coBrokers,
    }),
  replaceReinsurers: (
    operationId: string,
    reinsurers: { reinsurer_id: string; share_bps: number }[],
  ) =>
    api.put<QuoteReinsurerShare[]>(financeiroDaCotacao(operationId, '/reinsurers'), {
      reinsurers,
    }),
  summary: (operationId: string, format?: 'pdf' | 'docx') =>
    requestBlob(
      financeiroDaCotacao(
        operationId,
        `/summary/download${format ? `?format=${format}` : ''}`,
      ),
    ),
  acceptTerms: (operationId: string, acceptanceTermId: string) =>
    api.post<QuoteFinancial>(financeiroDaCotacao(operationId, '/terms-acceptance'), {
      acceptance_term_id: acceptanceTermId,
    }),
  validate: (operationId: string, kind: QuoteValidationKind) =>
    api.post<void>(financeiroDaCotacao(operationId, `/${kind}`), {}),
  retroactivity: (operationId: string, signal?: AbortSignal) =>
    api.get<QuoteRetroactivity>(
      financeiroDaCotacao(operationId, '/retroactivity-validation'),
      signal,
    ),
  coinsurance: (operationId: string, signal?: AbortSignal) =>
    api.get<QuoteCoinsurance>(
      financeiroDaCotacao(operationId, '/coinsurance-validation'),
      signal,
    ),
  coinsuranceHistory: (operationId: string, signal?: AbortSignal) =>
    api.get<QuoteCoinsuranceHistory>(
      financeiroDaCotacao(operationId, '/coinsurance-history'),
      signal,
    ),
  policyCoinsurance: (policyId: string, signal?: AbortSignal) =>
    api.get<QuoteCoinsuranceObject>(`/api/v1/policies/${policyId}/coinsurance`, signal),
  endorsementCoinsurance: (endorsementId: string, signal?: AbortSignal) =>
    api.get<QuoteCoinsuranceObject>(
      `/api/v1/endorsements/${endorsementId}/coinsurance`,
      signal,
    ),
  termCheck: (modalityId: string, termDays: number | null, signal?: AbortSignal) =>
    api.get<QuoteTermCheck>(
      `/api/v1/modalities/${modalityId}/term-validation${query({ term_days: termDays ?? undefined })}`,
      signal,
    ),
  /** Resseguradores do cadastro do P-PAR-4, para escolher na cotacao. */
  reinsurerOptions: (signal?: AbortSignal) =>
    api.get<{ id: string; legal_name: string; status: string }[]>(
      '/api/v1/reinsurers',
      signal,
    ),
  settings: (signal?: AbortSignal) =>
    api.get<QuoteFinancialSettings>('/api/v1/operations/financial-settings', signal),
  updateSettings: (
    input: Partial<
      Pick<
        QuoteFinancialSettings,
        'issuance_requires_terms_acceptance' | 'premium_insured_ratio_limit_bps'
      >
    >,
  ) => api.put<QuoteFinancialSettings>('/api/v1/operations/financial-settings', input),
};

// --- Cotacao da corretora e assistente de nova cotacao (P-NCT) --------------------
// Ver docs/ADR/0054-cotacao-da-corretora.md.

/** Canal e filial da cotacao (P-NCT). Opcionais para respostas antigas. */
export interface QuoteFields {
  /** Canal de venda da corretora na abertura. Nulo e "Canal nao definido". */
  channel?: PartyRef | null;
  /** Filial do tomador escolhida no assistente; `sublabel` e o CNPJ. */
  policyholder_branch?: PartyRef | null;
  /** Codigo do quadro de distribuicao (NCT-215). Vazio e "nao informado". */
  distribution_code?: string;
}

/** O que a sessao pode pedir na cotacao. A decisao continua do servidor. */
export interface QuoteActions {
  can_edit: boolean;
  can_submit: boolean;
  can_withdraw: boolean;
  can_request_issuance: boolean;
}

/** Configuracao da cotacao da corretora da empresa. */
export interface BrokerQuoteSettings {
  broker_rate_mode: 'sugerida' | 'faixa';
  broker_timeline_detail: 'resumo' | 'completo';
  broker_withdraw_scope: 'cotacao' | 'ate_emissao';
  updated_by_label?: string;
  updated_at?: string | null;
}

/** Regra do OnPoint que a cotacao prepara e nao aplica. */
export interface BrokerQuotePendingRule {
  code: string;
  description: string;
}

/** Modalidade ativa que o assistente oferece, com o grupo que filtra a lista. */
export interface BrokerQuoteModality {
  id: string;
  product: PartyRef;
  name: string;
  code: string;
  credit_group: string;
  group: PartyRef | null;
  allows_complementary: boolean;
  is_complementary: boolean;
  min_term_days: number | null;
  max_term_days: number | null;
  /** Modalidade de leiloeiro, da cotacao de pessoa fisica (P-NCT-3). */
  is_auctioneer?: boolean;
}

/** Classe de parcelamento ativa, so mostrada: nenhuma parcela e calculada. */
export interface BrokerQuoteInstallmentClass {
  id: string;
  code: string;
  name: string;
  max_installments: number | null;
  min_installment: Money | null;
  first_installment_min_days: number | null;
  first_installment_max_days: number | null;
  interest_from_installment: number | null;
  monthly_interest_bps: number | null;
}

/** O que o assistente le de uma vez. */
export interface BrokerQuoteOptions {
  settings: BrokerQuoteSettings;
  rate_editable: boolean;
  modalities: BrokerQuoteModality[];
  payment_methods: { id: string; label: string }[];
  installment_classes: BrokerQuoteInstallmentClass[];
  pending_rules: BrokerQuotePendingRule[];
  /**
   * Comissao padrao da empresa em pontos-base, so leitura: o unico numero da
   * configuracao da cotacao da empresa que a corretora recebe (OPEN_QUESTIONS 2.97). Sem
   * ele, a comissao sem percentual na corretora fica em branco, como antes.
   */
  default_commission_bps?: number;
}

export interface BrokerQuoteEstimateInput {
  modality_id: string;
  insured_amount: Money;
  applied_rate_bps?: number;
}

/** Estimativa do premio da modalidade principal, sem gravar. */
export interface BrokerQuoteEstimate {
  insured_amount: Money;
  rate: {
    applied_bps: number | null;
    source: 'informada' | 'sugerida' | 'sem_taxa';
    editable: boolean;
    suggested_bps: number | null;
    band: { min_rate_bps: number; max_rate_bps: number } | null;
    outside_band: boolean;
  };
  premium: Money | null;
  calculation: 'importancia_segurada_x_taxa';
  pending_rules: BrokerQuotePendingRule[];
  message: string;
}

/** Segurado que o passo "Dados do segurado" oferece. */
export interface BrokerQuoteInsuredOption {
  id: string;
  legal_name: string;
  social_name: string;
  document: string;
  addresses: { id: string; label: string; is_main: boolean }[];
}

/**
 * Opcoes da "Nova cotacao Pessoa Fisica" (a cotacao do leiloeiro, P-NCT-3). A geracao
 * vem bloqueada: a importancia segurada e da junta comercial, sem regra aprovada
 * (NCT-202). Ver docs/ADR/0066-cotacao-pessoa-fisica-e-leiloeiro.md.
 */
export interface IndividualQuoteOptions {
  enabled: boolean;
  disabled_reason: string;
  modalities: BrokerQuoteModality[];
  generation_blocked: boolean;
  block_reason: string;
  blocked_rules: string[];
  pending_rules: BrokerQuotePendingRule[];
}

/** Junta comercial cadastrada como segurado, com os enderecos na UF pedida. */
export interface CommercialRegistryOption {
  id: string;
  legal_name: string;
  document: string;
  email: string;
  blocked: boolean;
  addresses: {
    id: string;
    label: string;
    is_main: boolean;
    zip_code: string;
    street: string;
    number: string;
    complement: string;
    district: string;
    city: string;
    state: string;
  }[];
}

export const brokerQuotes = {
  individualOptions: (signal?: AbortSignal) =>
    api.get<IndividualQuoteOptions>(
      '/api/v1/operations/individual-quote-options',
      signal,
    ),
  commercialRegistries: (state: string, signal?: AbortSignal) =>
    api.get<CommercialRegistryOption[]>(
      `/api/v1/operations/commercial-registry-options${query({ state })}`,
      signal,
    ),
  options: (signal?: AbortSignal) =>
    api.get<BrokerQuoteOptions>('/api/v1/operations/quote-options', signal),
  updateSettings: (input: BrokerQuoteSettings) =>
    api.put<BrokerQuoteSettings>('/api/v1/operations/quote-settings', {
      broker_rate_mode: input.broker_rate_mode,
      broker_timeline_detail: input.broker_timeline_detail,
      broker_withdraw_scope: input.broker_withdraw_scope,
    }),
  estimate: (input: BrokerQuoteEstimateInput) =>
    api.post<BrokerQuoteEstimate>('/api/v1/operations/premium-estimate', input),
  insuredOptions: (q: string, signal?: AbortSignal) =>
    api.get<BrokerQuoteInsuredOption[]>(
      `/api/v1/operations/insured-options${query({ q })}`,
      signal,
    ),
  submit: (operationId: string) =>
    api.post<OperationDetail>(`/api/v1/operations/${operationId}/submission`),
  withdraw: (operationId: string, reason?: string) =>
    api.post<OperationDetail>(
      `/api/v1/operations/${operationId}/withdrawal`,
      reason ? { reason } : {},
    ),
  requestIssuance: (operationId: string, termAcceptanceId?: string) =>
    api.post<OperationDetail>(
      `/api/v1/operations/${operationId}/issuance-request`,
      termAcceptanceId ? { term_acceptance_id: termAcceptanceId } : {},
    ),
};
